import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

export interface CompactMultiselectOption {
  value: string | number;
  label: string;
  /** Kept from before but no longer offered (flagged in its label): it counts in neither "N selected" nor a full list. */
  isStale?: boolean;
}

/** An option as the list draws it. */
interface OptionRow {
  readonly option: CompactMultiselectOption;
  readonly isChecked: boolean;
}

let nextMultiselectId = 0;

// Compact multi-select: a pill showing the all/selected summary that opens a
// checkbox popover. An empty selection means "all".
//
// `variant` picks the trigger's box model. 'pill' sits inline at its own width;
// 'field' fills its column and lines up with native selects and date inputs.
// `opensUp` draws the popover above the trigger, for a control at the foot of a
// scrolling container, which would clip a popover that drops down. `allHint` is
// a note under the "all" option. `fullIsAll` reads every option ticked as All,
// for a filter where a full list and "all" run the same query; off, a full list
// reads "N selected". `emptyIsAll` off makes All a choice of its own, for a
// field where unticking the last option must not mean all: `allSelected` ticks
// it, picking it emits `allChosen` (never `selectedChange`), and an empty
// selection reads as none. `allDisabled` greys All out, `disabled` the whole
// control. `labelledBy` names the trigger by the field's label.
// `sheet` (opt-in, for a phone) opens the list as a full list instead: it fills
// the nearest positioned box around the control, under a head with `sheetTitle`
// (which names the list) and Done; focus moves to Done, and Done, Esc or Tab
// out of it closes it with focus back on the trigger. Options wrap and are at
// least 44px tall. A change of `sheet` while open closes it.
// While open, Esc closes the popover only, wherever focus is, and before any
// listener on the page sees it (its keydown is cancelled and its keyup stopped),
// so it never closes a dialog the control sits in; a click anywhere else
// closes it and still reaches what it was on. Opening one closes any other.
@Component({
  selector: 'app-compact-multiselect',
  templateUrl: './compact-multiselect.component.html',
  styleUrls: ['./compact-multiselect.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompactMultiselectComponent implements OnChanges, OnDestroy {
  @Input() options: CompactMultiselectOption[] = [];
  @Input() selected: Array<string | number> = [];
  @Input() allLabel = 'All';
  @Input() variant: 'pill' | 'field' = 'pill';
  @Input() opensUp = false;
  @Input() allHint: string | null = null;
  @Input() fullIsAll = true;
  @Input() emptyIsAll = true;
  @Input() allSelected = false;
  @Input() allDisabled = false;
  @Input() disabled = false;
  @Input() labelledBy: string | null = null;
  @HostBinding('class.ms--sheet') @Input() sheet = false;
  @Input() sheetTitle: string | null = null;
  @Output() selectedChange = new EventEmitter<Array<string | number>>();
  @Output() allChosen = new EventEmitter<void>();
  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('panel') private panel?: ElementRef<HTMLElement>;
  @ViewChild('done') private done?: ElementRef<HTMLButtonElement>;

  @HostBinding('class.ms--field') get isFieldVariant(): boolean {
    return this.variant === 'field';
  }

  private readonly uid = ++nextMultiselectId;
  readonly summaryId = `ms-${this.uid}-summary`;
  readonly titleId = `ms-${this.uid}-title`;
  open = false;
  summary = '';
  /** The field's label, then the summary: "Payer types, 2 selected". */
  triggerLabelledBy: string | null = null;
  isAllActive = false;
  rows: OptionRow[] = [];

  private readonly closeOnEscapeDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.open) { return; }
    event.preventDefault();
    this.zone.run(() => {
      this.setOpen(false);
      this.awaitEscapeUp();
      this.trigger?.nativeElement.focus();
      this.cdr.markForCheck();
    });
  };

  /** The keyup of the Esc that closed the list stops here, wherever focus is by then. */
  private readonly swallowEscapeUp = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') { return; }
    event.stopPropagation();
    this.stopAwaitingEscapeUp();
  };

  /** Any other key down means that Esc's keyup has gone by: stop waiting for it. */
  private readonly stopAwaitingOnNextKey = (): void => this.stopAwaitingEscapeUp();

  private readonly closeOnOutsideClick = (event: MouseEvent): void => {
    if (event.composedPath().includes(this.host.nativeElement)) { return; }
    this.zone.run(() => {
      this.setOpen(false);
      this.cdr.markForCheck();
    });
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.disabled) { this.setOpen(false); }
    if (changes.sheet && !changes.sheet.firstChange && this.open) { this.closeAcrossModes(); }
    const selected = this.selected || [];
    const checked = new Set(selected);
    this.rows = this.options.map(option => ({ option, isChecked: checked.has(option.value) }));
    this.isAllActive = this.emptyIsAll ? selected.length === 0 : this.allSelected;
    this.summary = this.summaryOf(selected);
    this.triggerLabelledBy = this.labelledBy ? `${this.labelledBy} ${this.summaryId}` : null;
  }

  toggleOpen(ev: Event): void {
    ev.stopPropagation();
    if (this.disabled) { return; }
    this.setOpen(!this.open);
    if (!this.sheet || !this.open) { return; }
    // The full list covers the trigger: focus moves into it, onto Done.
    this.cdr.detectChanges();
    this.done?.nativeElement.focus();
  }

  /** The full list's Done: it closes, focus back on the trigger. */
  finish(ev: Event): void {
    ev.stopPropagation();
    this.closeToTrigger();
  }

  /** Tab or Shift+Tab out of the full list closes it, focus back on the trigger, never on a field the list covers. */
  leaveOnTab(event: KeyboardEvent): void {
    if (!this.sheet || event.key !== 'Tab' || !this.panel) { return; }
    const stops = Array.from(this.panel.nativeElement.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
    const edge = event.shiftKey ? stops[0] : stops[stops.length - 1];
    if (this.document.activeElement !== edge) { return; }
    event.preventDefault();
    this.closeToTrigger();
  }

  close(): void {
    this.setOpen(false);
  }

  ngOnDestroy(): void {
    this.setOpen(false);
    this.stopAwaitingEscapeUp();
  }

  toggle(value: string | number, ev: Event): void {
    ev.stopPropagation();
    const next = (this.selected || []).slice();
    const i = next.indexOf(value);
    if (i === -1) next.push(value);
    else next.splice(i, 1);
    this.selectedChange.emit(next);
  }

  selectAll(ev: Event): void {
    ev.stopPropagation();
    if (this.allDisabled) { return; }
    if (this.emptyIsAll) {
      this.selectedChange.emit([]); // empty = all
    } else {
      this.allChosen.emit();
    }
  }

  trackByValue = (_: number, row: OptionRow) => row.option.value;

  /**
   * All, none, the one option's label, or how many are ticked. A stale option
   * counts neither in "N selected" nor towards a full list; a selection of
   * stale options only says so.
   */
  private summaryOf(selected: ReadonlyArray<string | number>): string {
    if (!this.emptyIsAll && this.allSelected) { return this.allLabel; }
    if (selected.length === 0) { return this.emptyIsAll ? this.allLabel : 'None selected'; }
    const labels = new Map(this.options.map(option => [option.value, option.label]));
    const stale = new Set(this.options.filter(option => option.isStale).map(option => option.value));
    const live = selected.filter(value => !stale.has(value));
    if (live.length === 0) {
      return selected.length === 1 ? labels.get(selected[0]) ?? '1 no longer listed' : `${selected.length} no longer listed`;
    }
    if (this.fullIsAll && live.length === this.options.length - stale.size) { return this.allLabel; }
    if (live.length === 1) { return labels.get(live[0]) ?? '1 selected'; }
    return `${live.length} selected`;
  }

  /** The page-wide listeners live only while the list is open; capture runs them before the page's own, outside Angular's zone. */
  private setOpen(open: boolean): void {
    if (open === this.open) { return; }
    this.open = open;
    if (open) {
      this.zone.runOutsideAngular(() => {
        this.document.addEventListener('keydown', this.closeOnEscapeDown, true);
        this.document.addEventListener('click', this.closeOnOutsideClick, true);
      });
    } else {
      this.document.removeEventListener('keydown', this.closeOnEscapeDown, true);
      this.document.removeEventListener('click', this.closeOnOutsideClick, true);
    }
  }

  private closeToTrigger(): void {
    this.setOpen(false);
    this.trigger?.nativeElement.focus();
    this.cdr.markForCheck();
  }

  /** Closed by a change of mode while open: focus goes to the trigger only from inside the list. */
  private closeAcrossModes(): void {
    const panel = this.panel?.nativeElement;
    const hadFocus = !!panel && panel.contains(this.document.activeElement);
    this.setOpen(false);
    if (hadFocus) { this.trigger?.nativeElement.focus(); }
  }

  private awaitEscapeUp(): void {
    this.zone.runOutsideAngular(() => {
      this.document.addEventListener('keyup', this.swallowEscapeUp, true);
      this.document.addEventListener('keydown', this.stopAwaitingOnNextKey, true);
    });
  }

  private stopAwaitingEscapeUp(): void {
    this.document.removeEventListener('keyup', this.swallowEscapeUp, true);
    this.document.removeEventListener('keydown', this.stopAwaitingOnNextKey, true);
  }
}
